import React, { useCallback, useEffect, useMemo, useState } from "react";
import "swiper/css";
import "swiper/css/pagination";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/react-splide/css";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import { useLanguage } from "../../context/LanguageContext";
import { getLocalizedFieldValue } from "../../utils/language";

const buildMediaUrl = (path = "") => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
  if (!base) return path;
  const normalizedBase = base.replace(/\/v1$/i, "");
  return `${normalizedBase}/${path.replace(/^\/+/, "")}`;
};

function PartnerStreams() {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(false);
  const { language } = useLanguage();
  const [copiedStreamUrl, setCopiedStreamUrl] = useState("");

  const normalizePartnerStreams = (data) => {
    if (!data) return [];
    if (Array.isArray(data?.partnerStream)) return data.partnerStream;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
  };

  const fetchPartnerStreams = useCallback(async () => {
    setLoading(true);
    try {
      const response = await handleApiCall("get", endpoints.partnerStreamList);
      if (
        response?.data?.code === 200 ||
        response?.data?.code === "200" ||
        response?.data?.success === true
      ) {
        const normalized = normalizePartnerStreams(response?.data?.data);
        setStreams(normalized);
      } else {
        console.error("Failed to fetch partner streams:", response?.message);
        setStreams([]);
      }
    } catch (error) {
      console.error("Error fetching partner streams:", error);
      setStreams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartnerStreams();
  }, [fetchPartnerStreams]);

  const slides = useMemo(() => {
    if (loading) {
      return [
        <SplideSlide key="loading" className="w-[320px] md:w-[450px] lg:w-[620px]">
          <div className="flex h-[330px] w-full items-center justify-center text-white text-lg bg-neutral-800 rounded-xl">
            {UI_CONSTANTS.BUTTON_LABELS.LOADING || "Loading..."}
          </div>
        </SplideSlide>,
      ];
    }

    if (!streams.length) {
      return [
        <SplideSlide key="empty" className="w-[320px] md:w-[450px] lg:w-[620px]">
          <div className="flex h-[330px] w-full items-center justify-center text-white text-lg bg-neutral-800 rounded-xl">
            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
          </div>
        </SplideSlide>,
      ];
    }

    const actionBtnClass =
    "mt-1 md:mt-4 md:px-5 md:py-2 inline-flex items-center justify-center " +
    "bg-saudiaGreen text-white rounded-sm md:rounded-md " +
    "w-[76px] md:w-[8.875rem] h-6 md:h-11 " +
    "text-[10px] md:text-lg font-SaudiaLight md:font-SaudiaLight";

  const handleCopyStreamUrl = async (url) => {
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    setCopiedStreamUrl(url);
    setTimeout(() => {
      setCopiedStreamUrl("");
    }, 2000);
  };


    return streams.map((item, index) => {
      const title =
        getLocalizedFieldValue(item, language, "title") ||
        UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND;
      // const videoUrl = buildMediaUrl(item?.video);
      // const targetUrl = item?.url ? item.url : videoUrl;

      const isVideo = item?.type === "video";
      const videoUrl = isVideo ? buildMediaUrl(item?.video) : null;
      const isImage = item?.type === "image";
      
      const imageUrl = isImage
        ? buildMediaUrl(item?.desktop_image)
        : null;

      const targetUrl = item?.url || videoUrl || imageUrl;      

      return (
        <SplideSlide key={item?._id || item?.id || index} className="w-[320px] md:w-[450px] lg:w-[620px]">
          <div className="relative h-[180px] md:h-[260px] lg:h-[330px] rounded-xl overflow-hidden bg-neutral-300 shadow-xl item">
            {/* {videoUrl ? (
              <video
                className="absolute inset-0 w-full h-full object-cover"
                src={videoUrl}
                autoPlay
                muted
                loop
                playsInline
                controls={false}
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80" />
            )} */}
             {isVideo && videoUrl ? (
              <video
                className="absolute inset-0 w-full h-full object-cover"
                src={videoUrl}
                autoPlay
                muted
                loop
                playsInline
                controls={false}
              />
            ) : isImage? (
                <picture className="absolute inset-0 w-full h-full object-cover">
                  <source media="(min-width: 768px)" srcSet={item.desktop_image} />
                  <img
                    src={item.mobile_image}
                    alt={title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </picture>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-neutral-800 text-white text-lg">
                {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80" />
          </div>
          {/* <div className="liveBtn flex items-center gap-x-1.5 bg-black absolute left-5 top-2 text-white px-1 rounded-[4px] text-sm">
            <span className="live-icon" />
            Live
          </div> */}
          {isVideo && videoUrl && (
            <div className="liveBtn flex items-center gap-x-1.5 bg-black absolute left-5 top-2 text-white px-1 rounded-[4px] text-sm">
              <span className="live-icon" />
              Live
            </div>
          )}
          <div className={`absolute caption px-3 ${language === "AR" ? "right-3 md:right-5" : "left-3 md:left-5"}`}>
            <h3 className="text-white text-lg md:text-[1.75rem] font-SaudiaBold">{title}</h3>
            {/* <a
              href={targetUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center mt-1 md:mt-4 md:px-5 md:py-2 bg-saudiaGreen text-white rounded-sm md:rounded-md w-[76px] md:w-[8.875rem] h-5 md:h-11 text-[10px] md:text-lg  font-SaudiaLight md:font-SaudiaRegular"
            >
              {language === "AR" ? "شاهد الآن" : (UI_CONSTANTS.BUTTON_LABELS.WATCH_NOW || "Watch Now")}
            </a> */}
            <div className="flex flex-col gap-1 mt-1 md:mt-4">
              <div className="flex gap-2">
                {/* WATCH NOW */}
                <a
                  href={targetUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={actionBtnClass}
                >
                  {language === "AR" ? "شاهد الآن" : "Watch Now"}
                </a>

                {/* COPY URL */}
                {/* <button
                  onClick={() => handleCopyStreamUrl(targetUrl)}
                  className={actionBtnClass}
                >
                  {language === "AR" ? "نسخ الرابط" : "Copy URL"}
                </button> */}
              </div>

              {/* COPIED TEXT */}
              {/* {copiedStreamUrl === targetUrl && (
                <span className="text-green-400 text-xs md:text-sm font-SaudiaRegular">
                  {language === "AR" ? "تم النسخ" : "Copied"}
                </span>
              )} */}
            </div>
          </div>
        </SplideSlide>
      );
    });
  }, [streams, loading, language]);

  return (
    <section className='overflow-hidden [&_.splide\_\_track]:!overflow-visible'>
      <div className='px-6 md:px-8 lg:px-8 2xl:px-0 max-w-8xl mx-auto'>
        <div className='md:ml-12 lg:ml-28 mb-10 md:mb-20 gap-x-16'>
          <h2 className={`text-white md:text-[2rem] lg:text-[2.625rem] mb-6 md:mb-8 ${language === "AR" ? "text-[22px] font-SaudiaBold":"text-[28px] font-SaudiaBoldItalic"}`}>
            {/* Partner Streams */}
            {/* {language === "AR" ? " بثّ الشركاء" : "Partner Streams"} */}
            {language === "AR" ? "البث المباشر لشراكاتنا" : "Partner Streams"}
          </h2>
          <div className='skewSlider nagative'>
            <Splide
              key={language}
              options={{
                rewind: true,
                gap: "20px",
                perPage: 2,
                autoWidth: true,
                focus: 0,
                perMove: 1,
                arrows: false,
                direction: language === "AR" ? "rtl" : "ltr",
                breakpoints: {
                  767: {
                    gap: "12px",
                  },
                },
              }}
              aria-label="Partner Streams"
            >
              {slides}
            </Splide>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PartnerStreams;

